import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    sendEmailVerification,
    sendPasswordResetEmail,
    setPersistence,
    browserLocalPersistence
} from "firebase/auth";
import { auth } from "../config/firebaseConfig";
import { getActionCodeSettings } from "./actionCodeSettings";
import { verificationInboxReminder } from "./emailDeliverability";

const DEBUG_MODE = process.env.NODE_ENV !== "production";

if (DEBUG_MODE) console.log("🔍 Initializing authService...");

(async () => {
    try {
        await setPersistence(auth, browserLocalPersistence);
        if (DEBUG_MODE) console.log("✅ Auth persistence set to local storage.");
    } catch (error) {
        console.error("❌ Auth persistence error:", error?.code || "auth_error");
    }
})();

export const registerUser = async (email, password) => {
    if (!email || !password) return { success: false, message: "Email and password are required." };

    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(userCredential.user, getActionCodeSettings());
        return { success: true, user: userCredential.user, message: `Verification email sent. ${verificationInboxReminder}` };
    } catch (error) {
        return { success: false, message: error.message };
    }
};

export const loginUser = async (email, password, { includeToken = false } = {}) => {
    if (!email || !password) {
        return {
            success: false,
            code: "auth/missing-credentials",
            message: "Email and password are required."
        };
    }

    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        if (!userCredential.user.emailVerified) {
            return {
                success: false,
                code: "auth/email-not-verified",
                message: "Email not verified."
            };
        }

        const response = {
            success: true,
            user: userCredential.user
        };

        if (includeToken) {
            response.token = await userCredential.user.getIdToken();
        }

        return response;
    } catch (error) {
        throw error;
    }
};

export const logoutUser = async () => {
    try {
        await signOut(auth);
        return { success: true, message: "User logged out." };
    } catch (error) {
        return { success: false, message: "Logout failed." };
    }
};

export const sendPasswordReset = async (email) => {
    if (!email) return { success: false, message: "Email is required." };

    try {
        await sendPasswordResetEmail(auth, email);
        return { success: true, message: "Password reset email sent." };
    } catch (error) {
        return { success: false, message: "Failed to send reset email." };
    }
};
